import { useState } from 'react';
import { X, Loader2, Ban } from 'lucide-react';

const STATUSES = [
  ['open', 'Open'],
  ['acknowledged', 'Seen'],
  ['in_progress', 'Being fixed'],
  ['resolved', 'Fixed'],
  ['closed', 'Closed'],
];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
const CLOSING = new Set(['resolved', 'closed']);

/**
 * Edit a ticket through its life.
 *
 * Only changed fields are sent. The API treats an absent key as "leave it
 * alone", so a status change cannot blank a description nobody touched — and
 * building the patch here rather than posting the whole form is what keeps two
 * attendants editing the same ticket from overwriting each other's text.
 */
export default function TicketEditor({ ticket, staff, onClose, onSave }) {
  const [form, setForm] = useState({
    status: ticket.status,
    priority: ticket.priority,
    title: ticket.title,
    body: ticket.body ?? '',
    blocks_rental: ticket.blocks_rental,
    resolution: ticket.resolution ?? '',
    assigned_to: ticket.assigned_to_public_id ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (e) => setForm((f) => ({
    ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const closing = CLOSING.has(form.status) && !CLOSING.has(ticket.status);
  const needsResolution = closing && !form.resolution.trim();

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true); setError(null);
    try {
      const patch = {};
      if (form.status !== ticket.status) patch.status = form.status;
      if (form.priority !== ticket.priority) patch.priority = form.priority;
      if (form.title !== ticket.title) patch.title = form.title;
      if (form.body !== (ticket.body ?? '')) patch.body = form.body;
      if (form.blocks_rental !== ticket.blocks_rental) patch.blocks_rental = form.blocks_rental;
      if (form.resolution !== (ticket.resolution ?? '')) patch.resolution = form.resolution;
      if (form.assigned_to !== (ticket.assigned_to_public_id ?? '')) {
        patch.assigned_to = form.assigned_to || null;
      }
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await onSave(patch);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4"
         role="dialog" aria-modal="true" aria-labelledby="ticket-editor-title">
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 id="ticket-editor-title" className="text-base font-extrabold tracking-tight text-slate-900">
            {ticket.unit_number ? `Unit ${ticket.unit_number}` : 'Site-wide issue'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close"
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {error && <p role="alert" className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

        <label className="field-label mb-1" htmlFor="t-title">Title</label>
        <input id="t-title" required minLength={3} maxLength={180} className="field-input"
               value={form.title} onChange={set('title')} />

        <label className="field-label mb-1 mt-3" htmlFor="t-body">Detail</label>
        <textarea id="t-body" rows={3} maxLength={4000} className="field-input"
                  value={form.body} onChange={set('body')} />

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="field-label mb-1" htmlFor="t-status">Status</label>
            <select id="t-status" className="field-input" value={form.status} onChange={set('status')}>
              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="t-priority">Priority</label>
            <select id="t-priority" className="field-input" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
        </div>

        <label className="field-label mb-1 mt-3" htmlFor="t-assignee">Assigned to</label>
        <select id="t-assignee" className="field-input" value={form.assigned_to} onChange={set('assigned_to')}>
          <option value="">Nobody yet</option>
          {staff.map((s) => (
            <option key={s.public_id} value={s.public_id}>
              {s.first_name} {s.last_name} ({s.role === 'super_admin' ? 'owner' : s.role})
            </option>
          ))}
        </select>

        <label htmlFor="t-blocks" className="mt-4 flex items-start gap-2 text-sm text-slate-800">
          <input id="t-blocks" type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600"
                 checked={form.blocks_rental} onChange={set('blocks_rental')} />
          <span>
            <span className="inline-flex items-center gap-1 font-semibold">
              <Ban className="h-3.5 w-3.5 text-rose-600" aria-hidden="true" /> Space cannot be rented
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Takes the space off the market while this is open, and puts it back when the last
              blocking issue is fixed.
            </span>
          </span>
        </label>

        {(CLOSING.has(form.status) || form.resolution) && (
          <>
            <label className="field-label mb-1 mt-4" htmlFor="t-resolution">What was done</label>
            <textarea id="t-resolution" rows={2} maxLength={2000} className="field-input"
                      value={form.resolution} onChange={set('resolution')}
                      aria-invalid={needsResolution || undefined}
                      placeholder="Repaved the north edge" />
            {needsResolution && (
              <p className="mt-1 text-xs text-rose-700">
                Say what was done — the next person needs to know why the space came back.
              </p>
            )}
          </>
        )}

        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={saving || needsResolution} className="btn-primary flex-1">
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
